/**
 * Shared image-upload helper — produces specific, human-friendly
 * error messages so admins know EXACTLY why an upload failed
 * (file too big, wrong format, network drop, session expired, …)
 * instead of a generic "Upload failed".
 *
 * Used by every upload site: `ImageUploader`, `MultiImageUploader`,
 * AdminBranding, SuperAdminSettings, seller product form, etc.
 *
 * Contract:
 *   await uploadImage(file, { endpoint = "/uploads", extraForm = {} })
 *     → resolves with the parsed API response ({ url, ... })
 *     → rejects with an Error whose `.message` is the friendly text
 *       already suitable for `toast.error(msg)`.
 */
import { api } from "./api";

// Kept in sync with /app/backend/app/routes/uploads.py:
//   ALLOWED_EXT = {jpg jpeg png webp gif heic heif}
//   MAX_BYTES   = 10 MB
const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"];
export const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

function humanSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

/**
 * Validate a File before hitting the network — a clean rejection
 * here is FAR cheaper than uploading 15 MB just to be told "too
 * big" over 4G.
 * @returns {string|null} error message, or null when valid.
 */
export function validateImageFile(file) {
  if (!file) return "No file selected";
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  if (!ALLOWED_EXT.includes(ext)) {
    return `Unsupported format ".${ext || "?"}". Use JPG, PNG, WebP, GIF or HEIC.`;
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    return `Image too large (${humanSize(file.size)}). Maximum size is 10 MB — please compress or crop the image.`;
  }
  if (file.size <= 0) {
    return "The selected file appears to be empty.";
  }
  return null;
}

/**
 * Map an axios/network error to a friendly string.
 * Exported so callers doing manual `api.post()` can reuse it.
 */
export function friendlyUploadError(err, file) {
  // Network layer — no HTTP response at all.
  if (err?.code === "ERR_NETWORK" || !err?.response) {
    if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
      return "Upload timed out — check your internet connection and try again.";
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "You appear to be offline. Please reconnect and try again.";
    }
    return "Network error — please check your connection and try again.";
  }
  const status = err.response.status;
  const detail = err.response?.data?.detail;
  const detailStr = typeof detail === "string"
    ? detail
    : Array.isArray(detail) && detail[0]?.msg
      ? detail[0].msg
      : "";
  if (status === 401 || status === 403) {
    return "Your session has expired. Please sign in again.";
  }
  if (status === 413 || /too large/i.test(detailStr) || /max.*10\s*mb/i.test(detailStr)) {
    return file
      ? `Image too large (${humanSize(file.size)}). Maximum size is 10 MB — please compress or crop the image.`
      : "Image too large — maximum size is 10 MB.";
  }
  if (status === 415 || /unsupported.*(file|format|type)/i.test(detailStr)) {
    return detailStr || "Unsupported file type. Use JPG, PNG, WebP, GIF or HEIC.";
  }
  if (status === 400 && /invalid image/i.test(detailStr)) {
    // Backend PIL couldn't decode — likely a corrupt file.
    return "This image couldn't be read — the file may be corrupted or in an unsupported format.";
  }
  if (status === 400) {
    return detailStr || "The upload was rejected by the server.";
  }
  if (status === 503) {
    return "The image storage service is temporarily unavailable. Please try again in a moment.";
  }
  if (status >= 500) {
    return "The server hit an unexpected error while saving the image. Please try again.";
  }
  return detailStr || `Upload failed (${status}).`;
}

/**
 * Validated + friendly-error-wrapped upload.
 * @param {File} file
 * @param {{endpoint?: string, extraForm?: Record<string, string>}} opts
 * @returns {Promise<object>} the server response ({url, ...})
 */
export async function uploadImage(file, opts = {}) {
  const err = validateImageFile(file);
  if (err) throw new Error(err);
  const endpoint = opts.endpoint || "/uploads";
  const fd = new FormData();
  fd.append("file", file);
  for (const [k, v] of Object.entries(opts.extraForm || {})) {
    if (v !== undefined && v !== null) fd.append(k, v);
  }
  try {
    const { data } = await api.post(endpoint, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      // 60s: enough for a 10 MB file over a slow 3G connection.
      timeout: 60000,
    });
    return data;
  } catch (e) {
    throw new Error(friendlyUploadError(e, file));
  }
}
